import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, ShieldCheckIcon, CheckCircleIcon, ChevronDownIcon, InfoIcon, TrendingUpIcon, BriefcaseIcon, CarIcon, HeartPulseIcon, HomeIcon, StethoscopeIcon, SmartphoneIcon, PlaneIcon, ArrowRightIcon, TargetIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface ProductDetailProps {
  navigate: (screen: string, productId?: string) => void;
  productId?: string;
}
type CalculatorType = 'savings' | 'quote';
interface ProductConfig {
  id: string;
  title: string;
  subtitle: string;
  icon: any;
  gradientFrom: string;
  gradientTo: string;
  iconBg: string;
  iconColor: string;
  badgeText: string;
  calculatorType: CalculatorType;
  calculatorTitle: string;
  calculatorDesc: string;
  inputLabel: string;
  inputMin: number;
  inputMax: number;
  inputStep: number;
  defaultInput: number;
  durationLabel?: string;
  durationMin?: number;
  durationMax?: number;
  defaultDuration?: number;
  features: string[];
  faqs: {
    q: string;
    a: string;
  }[];
  claimsFlow: string[];
}
const productConfigs: Record<string, ProductConfig> = {
  goalguard: {
    id: 'goalguard',
    title: 'GoalGuard',
    subtitle: 'Save towards any goal with guaranteed protection.',
    icon: ShieldCheckIcon,
    gradientFrom: 'from-emerald-600',
    gradientTo: 'to-teal-800',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
    badgeText: 'Flagship • Protection-Backed Savings',
    calculatorType: 'savings',
    calculatorTitle: 'Goal Calculator',
    calculatorDesc: 'Set your target and see how much to save monthly.',
    inputLabel: 'Target Savings Goal',
    inputMin: 500000,
    inputMax: 50000000,
    inputStep: 500000,
    defaultInput: 5000000,
    durationLabel: 'Timeline (Years)',
    durationMin: 1,
    durationMax: 20,
    defaultDuration: 5,
    features: ['Guaranteed payout if you pass away before reaching your goal', 'Earn up to 12% projected annual returns', 'Flexible contributions — save at your own pace', 'Withdraw anytime after 6 months with no penalty'],
    faqs: [{
      q: 'What makes GoalGuard different from regular savings?',
      a: 'GoalGuard combines disciplined savings with life cover. If anything happens to you, your beneficiary receives the full target amount — not just what you have saved so far.'
    }, {
      q: 'Can I have multiple GoalGuard plans?',
      a: 'Yes! You can create separate GoalGuard plans for different goals — a car, a home, education, or anything else.'
    }],
    claimsFlow: ['User submits claim in-app and uploads required documents.', 'System validates completeness.', 'Skydd Ops receives notification and reviews.', 'Claim bundle forwarded offline to Tangerine Life.', 'Insurer reviews/investigates.', 'If more info is needed, the user is contacted.', 'Claim approved or rejected.', 'If approved, payout sent to wallet/bank.', 'Claim closed and policy updated.']
  },
  'child-education': {
    id: 'child-education',
    title: 'Future Scholar Fund',
    subtitle: 'Secure their future today.',
    icon: GraduationCapIcon,
    gradientFrom: 'from-emerald-600',
    gradientTo: 'to-emerald-800',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
    badgeText: 'Protection-Backed Savings',
    calculatorType: 'savings',
    calculatorTitle: 'Education Fund Calculator',
    calculatorDesc: 'See how much you need to save to reach your goal.',
    inputLabel: 'Target Education Fund',
    inputMin: 1000000,
    inputMax: 50000000,
    inputStep: 500000,
    defaultInput: 10000000,
    durationLabel: 'Years until University',
    durationMin: 1,
    durationMax: 18,
    defaultDuration: 18,
    features: ['Guaranteed ₦10M payout if you pass away', 'Earn up to 12% projected annual returns', 'Flexible monthly or annual contributions', 'Withdrawals allowed for school fees'],
    faqs: [{
      q: 'What happens if I die before maturity?',
      a: "The full target amount is immediately guaranteed and paid out to your named beneficiary, ensuring your child's education is secured regardless."
    }, {
      q: 'What happens if I miss a payment?',
      a: 'You have a 30-day grace period. After that, your cover may be suspended until arrears are cleared, but your accumulated savings remain yours.'
    }, {
      q: 'Can I change my target amount later?',
      a: 'Yes, you can increase your target amount at any policy anniversary, which will adjust your monthly contribution accordingly.'
    }],
    claimsFlow: ['User submits death or permanent disability claim with required documents.', 'Skydd Ops reviews documents.', 'Claim forwarded offline to Tangerine Life.', 'Insurer assesses claim.', 'Approval/rejection communicated.', 'If approved, the sum assured + bonus is paid out to the beneficiary.', 'Policy closed.']
  },
  'job-loss': {
    id: 'job-loss',
    title: 'Income Shield',
    subtitle: 'Income protection if you lose your job.',
    icon: BriefcaseIcon,
    gradientFrom: 'from-blue-600',
    gradientTo: 'to-blue-800',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-500',
    badgeText: 'Income Protection',
    calculatorType: 'quote',
    calculatorTitle: 'Coverage Estimator',
    calculatorDesc: 'Estimate your contribution based on your current salary.',
    inputLabel: 'Current Monthly Salary',
    inputMin: 100000,
    inputMax: 5000000,
    inputStep: 50000,
    defaultInput: 500000,
    features: ['Up to 6 months of income replacement', 'Covers 60% of your monthly salary', 'Includes career coaching and CV review', 'Fast payout within 48 hours of claim approval'],
    faqs: [{
      q: 'What counts as involuntary job loss?',
      a: 'Redundancy, company closure, or restructuring. It does not cover voluntary resignation, retirement, or dismissal for gross misconduct.'
    }, {
      q: 'Is there a waiting period?',
      a: 'Yes, there is a standard 90-day waiting period from the plan start date before you can make a claim.'
    }],
    claimsFlow: ['User submits involuntary job loss claim.', 'Upload termination letter, payslips, employer confirmation, statements.', 'Skydd verifies termination was involuntary.', 'Verify 6-month qualifying premium period completed.', 'Verify waiting period passed.', 'Employer HR contacted for confirmation.', 'Claim sent to Tangerine General offline.', 'If approved, first payout starts after 30 days.', 'Monthly continued unemployment checks before each payout.', 'Continue up to 6 months max.']
  },
  motor: {
    id: 'motor',
    title: 'AutoGuard',
    subtitle: 'Comprehensive cover + savings.',
    icon: CarIcon,
    gradientFrom: 'from-indigo-600',
    gradientTo: 'to-indigo-800',
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-500',
    badgeText: 'Asset Protection',
    calculatorType: 'quote',
    calculatorTitle: 'Contribution Estimator',
    calculatorDesc: 'Estimate your comprehensive protection contribution.',
    inputLabel: 'Estimated Vehicle Value',
    inputMin: 1000000,
    inputMax: 100000000,
    inputStep: 500000,
    defaultInput: 5000000,
    features: ['Comprehensive and Third-Party options', 'Optional savings add-on for vehicle upgrades', 'Free towing service within major cities', 'Instant digital certificate and sticker'],
    faqs: [{
      q: 'What does comprehensive cover include?',
      a: 'It covers accidental damage to your vehicle, theft, fire, and third-party property damage or bodily injury.'
    }, {
      q: 'How does the savings add-on work?',
      a: 'You can add a monthly savings amount to your contribution. This builds a fund you can use for future maintenance or a down payment on your next car.'
    }],
    claimsFlow: ['User reports accident/loss claim.', 'Upload claims form, repair estimate, and police report where required.', 'Skydd Ops reviews submission.', 'Claim forwarded to Tangerine General offline.', 'Vehicle/incident assessed.', 'Approval/rejection issued.', 'Repair reimbursement or settlement processed.']
  },
  life: {
    id: 'life',
    title: 'Legacy Plan',
    subtitle: 'Pure protection + long-term savings.',
    icon: HeartPulseIcon,
    gradientFrom: 'from-rose-600',
    gradientTo: 'to-rose-800',
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-500',
    badgeText: 'Endowment Plan',
    calculatorType: 'savings',
    calculatorTitle: 'Endowment Calculator',
    calculatorDesc: 'Calculate your contribution for life cover and savings.',
    inputLabel: 'Desired Guaranteed Amount',
    inputMin: 5000000,
    inputMax: 100000000,
    inputStep: 1000000,
    defaultInput: 20000000,
    durationLabel: 'Plan Term (Years)',
    durationMin: 5,
    durationMax: 30,
    defaultDuration: 20,
    features: ['Pays out on death or permanent disability', 'Accumulates cash value over time', 'Tax-free maturity payout', 'Option to add critical illness cover'],
    faqs: [{
      q: 'What is an endowment plan?',
      a: 'It is a life protection plan designed to pay a lump sum after a specific term (on its maturity) or on death. Typical maturities are ten, fifteen or twenty years up to a certain age limit.'
    }, {
      q: 'Can I borrow against my plan?',
      a: 'Yes, after the plan has acquired a cash value (usually after 3 years), you can take a plan loan up to a certain percentage of the cash value.'
    }],
    claimsFlow: ['User/beneficiary submits claim (death, disability, or critical illness).', 'Required documents uploaded.', 'Skydd Ops reviews.', 'Claim forwarded offline to Tangerine/Crystalife.', 'Insurer assesses claim.', 'If approved, payout made to beneficiary/user.', 'Policy updated/closed if necessary.']
  },
  annuity: {
    id: 'annuity',
    title: 'Freedom Fund',
    subtitle: 'Guaranteed income for life.',
    icon: TrendingUpIcon,
    gradientFrom: 'from-purple-600',
    gradientTo: 'to-purple-800',
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-500',
    badgeText: 'Retirement Planning',
    calculatorType: 'savings',
    calculatorTitle: 'Retirement Calculator',
    calculatorDesc: 'Plan your contributions for a comfortable retirement.',
    inputLabel: 'Target Monthly Retirement Income',
    inputMin: 50000,
    inputMax: 2000000,
    inputStep: 50000,
    defaultInput: 300000,
    durationLabel: 'Years until Retirement',
    durationMin: 5,
    durationMax: 40,
    defaultDuration: 25,
    features: ['Guaranteed regular income post-retirement', 'Protects against outliving your savings', 'Flexible contribution options during accumulation', 'Joint life options available for spouses'],
    faqs: [{
      q: 'How long can I expect to receive the retirement fund?',
      a: 'The Freedom Fund provides a guaranteed income for life. You will continue to receive regular payouts for as long as you live, ensuring you never outlive your savings.'
    }, {
      q: 'What happens if I die shortly after retiring?',
      a: 'Depending on the plan selected, a guaranteed period (e.g., 10 years) ensures that if you pass away early, the remaining payments for that period are made to your beneficiaries.'
    }],
    claimsFlow: ['Claim initiated for death or disability before retirement.', 'User/beneficiary uploads documents.', 'Skydd Ops validates records.', 'Fund value calculated.', 'Claim sent to Tangerine Life.', 'Approval processed.', 'Fund value paid to beneficiary/user.', 'Policy closed.']
  },
  home: {
    id: 'home',
    title: 'Property Shield',
    subtitle: 'Protect property & save for upgrades.',
    icon: HomeIcon,
    gradientFrom: 'from-amber-600',
    gradientTo: 'to-amber-800',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    badgeText: 'Property Protection',
    calculatorType: 'quote',
    calculatorTitle: 'Contribution Estimator',
    calculatorDesc: 'Estimate your property protection contribution.',
    inputLabel: 'Estimated Property/Contents Value',
    inputMin: 2000000,
    inputMax: 200000000,
    inputStep: 1000000,
    defaultInput: 15000000,
    features: ['Covers building and contents against fire, flood, theft', 'Optional savings plan for home improvements', 'Alternative accommodation cover if home is uninhabitable', 'Personal liability cover included'],
    faqs: [{
      q: 'Do I need to own the home to buy this?',
      a: 'No, renters can purchase contents-only protection to protect their personal belongings, while landlords can purchase building-only or comprehensive cover.'
    }],
    claimsFlow: ['User reports home contents loss/damage.', 'Upload evidence/documents.', 'Skydd Ops reviews incident.', 'Claim sent to insurer.', 'Assessment of fire/theft/flood/storm loss.', 'Approved payout issued up to insured limit.']
  },
  health: {
    id: 'health',
    title: 'Wellness Plan',
    subtitle: 'HMO and indemnity plans.',
    icon: StethoscopeIcon,
    gradientFrom: 'from-cyan-600',
    gradientTo: 'to-cyan-800',
    iconBg: 'bg-cyan-50',
    iconColor: 'text-cyan-500',
    badgeText: 'Health & Wellness',
    calculatorType: 'quote',
    calculatorTitle: 'Plan Estimator',
    calculatorDesc: 'Estimate contribution based on desired annual limit.',
    inputLabel: 'Desired Annual Medical Limit',
    inputMin: 500000,
    inputMax: 10000000,
    inputStep: 500000,
    defaultInput: 2000000,
    features: ['Access to top hospitals nationwide', 'Covers outpatient, inpatient, and maternity', 'Optional wellness savings component', '24/7 telemedicine access'],
    faqs: [{
      q: 'Can I add my family members?',
      a: 'Yes, family plans are available covering a principal enrollee, spouse, and up to 4 children under 18.'
    }, {
      q: 'Are pre-existing conditions covered?',
      a: 'Coverage for pre-existing conditions typically begins after a waiting period (usually 6-12 months), depending on the specific plan tier.'
    }],
    claimsFlow: ['User requests covered treatment or files reimbursement claim depending on sub-type.', 'Validate active cover and treatment eligibility.', 'Provider/HMO confirms treatment.', 'Claim processed under plan rules.', 'Payment to provider or reimbursement to user.']
  },
  travel: {
    id: 'travel',
    title: 'TravelGuard',
    subtitle: 'Comprehensive travel protection.',
    icon: PlaneIcon,
    gradientFrom: 'from-slate-600',
    gradientTo: 'to-slate-800',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    badgeText: 'Travel Protection',
    calculatorType: 'quote',
    calculatorTitle: 'Contribution Estimator',
    calculatorDesc: 'Estimate your travel protection contribution.',
    inputLabel: 'Trip Duration (Days)',
    inputMin: 1,
    inputMax: 90,
    inputStep: 1,
    defaultInput: 14,
    features: ['Local and International cover options', 'Medical emergencies and evacuation', 'Trip cancellation and delay cover', 'Lost baggage and passport assistance'],
    faqs: [{
      q: 'Does this cover pre-existing medical conditions?',
      a: 'Standard plans cover acute onset of pre-existing conditions during travel, subject to policy limits.'
    }],
    claimsFlow: ['User reports travel incident (delay, cancellation, medical issue, accident).', 'Upload travel documents and proof.', 'Skydd Ops reviews claim.', 'Insurer validates travel dates/event.', 'If approved, reimbursement/benefit paid.', 'Claim closed.']
  }
};
export function ProductDetail({
  navigate,
  productId = 'goalguard'
}: ProductDetailProps) {
  const config = productConfigs[productId] || productConfigs['goalguard'];
  const [inputValue, setInputValue] = useState<number>(config.defaultInput);
  const [durationValue, setDurationValue] = useState<number>(config.defaultDuration || 1);
  const [monthlyPremium, setMonthlyPremium] = useState<number>(0);
  const [totalContributions, setTotalContributions] = useState<number>(0);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  // Reset state when product changes
  useEffect(() => {
    setInputValue(config.defaultInput);
    if (config.defaultDuration) setDurationValue(config.defaultDuration);
  }, [productId, config]);
  // Savings Calculator Logic
  useEffect(() => {
    if (config.calculatorType === 'savings') {
      const r = 0.1 / 12; // 10% annual
      const n = durationValue * 12;
      if (n > 0) {
        const pmt = inputValue * (r / (Math.pow(1 + r, n) - 1));
        setMonthlyPremium(pmt);
        setTotalContributions(pmt * n);
      }
    } else {
      // Simple Quote Logic
      let rate = 0.02;
      if (productId === 'job-loss') rate = 0.1; // 10% of salary
      if (productId === 'travel') rate = 0.05; // 5% of trip duration/value approx
      if (productId === 'health') rate = 0.08; // 8% of limit
      if (productId === 'motor') rate = 0.035; // 3.5% of vehicle value
      if (productId === 'home') rate = 0.005; // 0.5% of property value
      let annualPremium = inputValue * rate;
      if (productId === 'travel') {
        // For travel, input is days. Let's say 2000 per day
        annualPremium = inputValue * 2000;
        setMonthlyPremium(annualPremium); // One off payment
      } else {
        setMonthlyPremium(annualPremium / 12);
      }
    }
  }, [inputValue, durationValue, config, productId]);
  const calcFV = (rate: number) => {
    const n = durationValue * 12;
    const r = rate / 12;
    return monthlyPremium * ((Math.pow(1 + r, n) - 1) / r);
  };
  const scenarios = [{
    label: 'Conservative (8%)',
    value: calcFV(0.08),
    color: 'bg-gray-400'
  }, {
    label: 'Moderate (10%)',
    value: calcFV(0.1),
    color: 'bg-emerald-500'
  }, {
    label: 'Optimistic (12%)',
    value: calcFV(0.12),
    color: 'bg-amber-500'
  }];
  const maxScenarioValue = scenarios[2].value;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-6 bg-slate-50" data-id="element-2465">
      {/* Hero Banner */}
      <div className={`bg-gradient-to-b ${config.gradientFrom} ${config.gradientTo} pt-safe pb-8 px-5 relative overflow-hidden rounded-b-[2.5rem] shadow-lg`} data-id="element-2466">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-2467"></div>

        <div className="relative z-20" data-id="element-2468">
          <TopBar showBack onBack={() => navigate('products')} transparent data-id="element-2469" />
        </div>

        <div className="relative z-10 mt-2 text-center" data-id="element-2470">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center mx-auto mb-4 border border-white/30 shadow-xl" data-id="element-2471">
            <config.icon className="w-10 h-10 text-white" data-id="element-2472" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-2473">
            {config.title}
          </h1>
          <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-3 py-1 rounded-full text-white/90 text-xs font-bold tracking-wide backdrop-blur-sm" data-id="element-2474">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-2475" /> {config.badgeText}
          </div>
        </div>
      </div>

      <div className="px-5 pt-4 pb-24 md:max-w-5xl md:mx-auto md:grid md:grid-cols-2 md:gap-8 md:items-start md:pb-8" data-id="element-2476">
        <div className="flex flex-col" data-id="element-2477">
          {/* Description Section */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6 text-center" data-id="element-2478">
            <div className={`w-16 h-16 rounded-2xl ${config.iconBg} flex items-center justify-center ${config.iconColor} mx-auto mb-4`} data-id="element-2479">
              <config.icon className="w-8 h-8" data-id="element-2480" />
            </div>
            <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-2481">
              {config.subtitle}
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed" data-id="element-2482">
              {config.calculatorType === 'savings' ? `A disciplined savings plan combined with robust guaranteed protection. Achieve your goals with peace of mind.` : `Comprehensive protection tailored to your needs. Get covered instantly and manage everything from your app.`}
            </p>
          </div>

          {/* Calculator Section */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6" data-id="element-2483">
            <h3 className="font-bold text-[#0D3B3B] mb-1" data-id="element-2484">
              {config.calculatorTitle}
            </h3>
            <p className="text-xs text-gray-500 mb-6" data-id="element-2485">
              {config.calculatorDesc}
            </p>

            <div className="space-y-6" data-id="element-2486">
              <div data-id="element-2487">
                <div className="flex justify-between mb-2" data-id="element-2488">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-2489">
                    {config.inputLabel}
                  </label>
                  <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2490">
                    {productId === 'travel' ? `${inputValue} Days` : formatNaira(inputValue)}
                  </span>
                </div>
                <input type="range" min={config.inputMin} max={config.inputMax} step={config.inputStep} value={inputValue} onChange={e => setInputValue(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500" data-id="element-2491" />
              </div>

              {config.calculatorType === 'savings' && config.durationLabel && <div data-id="element-2492">
                  <div className="flex justify-between mb-2" data-id="element-2493">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-2494">
                      {config.durationLabel}
                    </label>
                    <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2495">
                      {durationValue} Years
                    </span>
                  </div>
                  <input type="range" min={config.durationMin} max={config.durationMax} step={1} value={durationValue} onChange={e => setDurationValue(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500" data-id="element-2496" />
                </div>}

              <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100" data-id="element-2497">
                <div className="text-center" data-id="element-2498">
                  <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1" data-id="element-2499">
                    {productId === 'travel' ? 'Estimated Total Premium' : 'Estimated Monthly Contribution'}
                  </p>
                  <h4 className="text-3xl font-bold text-emerald-600" data-id="element-2500">
                    {formatNaira(monthlyPremium)}
                  </h4>
                  {config.calculatorType === 'savings' ? <p className="text-xs text-emerald-700 mt-2" data-id="element-2501">
                      Total Contribution: {formatNaira(totalContributions)}
                    </p> : <p className="text-[10px] text-emerald-700 mt-2 leading-tight" data-id="element-2502">
                      *This is an estimate. Actual pricing depends on the
                      specific plan and coverage level you select.
                    </p>}
                </div>
              </div>
            </div>
          </div>

          {/* Scenarios (Only for Savings) */}
          {config.calculatorType === 'savings' && <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6" data-id="element-2503">
              <h3 className="font-bold text-[#0D3B3B] mb-1" data-id="element-2504">
                Projected Growth
              </h3>
              <p className="text-xs text-gray-500 mb-6" data-id="element-2505">
                Estimated maturity value based on market performance.
              </p>

              <div className="space-y-4" data-id="element-2506">
                {scenarios.map((scenario, idx) => <div key={idx} data-id="element-2507">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-2508">
                      <span className="font-medium text-gray-600" data-id="element-2509">
                        {scenario.label}
                      </span>
                      <span className="font-bold text-[#0D3B3B]" data-id="element-2510">
                        {formatNaira(scenario.value)}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-2511">
                      <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: `${scenario.value / maxScenarioValue * 100}%`
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 1,
                  delay: idx * 0.2
                }} className={`h-full ${scenario.color} rounded-full`} data-id="element-2512" />
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* CTA - visible on mobile */}
          <div className="mb-8 md:hidden" data-id="element-2513">
            <button onClick={() => navigate('plan-selection', productId)} className="w-full bg-[#0D3B3B] hover:bg-[#092D2D] text-white font-bold py-4 rounded-xl shadow-lg shadow-emerald-900/20 active:scale-95 transition-transform flex items-center justify-center gap-2" data-id="element-2514">
              View Available Plans <ArrowRightIcon className="w-5 h-5" data-id="element-2515" />
            </button>
          </div>
        </div>

        {/* Right Column on web */}
        <div className="flex flex-col md:sticky md:top-4" data-id="element-2516">
          {/* Features */}
          <div className="mb-8" data-id="element-2517">
            <h3 className="font-bold text-[#0D3B3B] mb-4 px-2" data-id="element-2518">Key Benefits</h3>
            <div className="space-y-3" data-id="element-2519">
              {config.features.map((feature, idx) => <div key={idx} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-3" data-id="element-2520">
                  <CheckCircleIcon className={`w-5 h-5 ${config.iconColor} shrink-0`} data-id="element-2521" />
                  <p className="text-sm text-gray-700 font-medium" data-id="element-2522">{feature}</p>
                </div>)}
            </div>
          </div>

          {/* Claims Process */}
          <div className="mb-8" data-id="element-2523">
            <h3 className="font-bold text-[#0D3B3B] mb-4 px-2" data-id="element-2524">
              Claims Process
            </h3>
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm" data-id="element-2525">
              <div className="relative pl-8" data-id="element-2526">
                <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#0D3B3B]/20 via-[#0D3B3B]/10 to-transparent" data-id="element-2527"></div>
                <div className="space-y-5" data-id="element-2528">
                  {config.claimsFlow.map((step, idx) => <div key={idx} className="relative flex items-start gap-4" data-id="element-2529">
                      <div className="absolute -left-8 flex items-center justify-center w-8 h-8 rounded-full border-[3px] border-white bg-[#0D3B3B] text-[#FFD700] text-xs font-bold shadow shrink-0 z-10" data-id="element-2530">
                        {idx + 1}
                      </div>
                      <div className="flex-1 p-3 rounded-xl border border-gray-100 bg-gray-50" data-id="element-2531">
                        <p className="text-sm text-gray-700 font-medium" data-id="element-2532">
                          {step}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-8" data-id="element-2533">
            <h3 className="font-bold text-[#0D3B3B] mb-4 px-2" data-id="element-2534">
              Frequently Asked Questions
            </h3>
            <div className="space-y-3" data-id="element-2535">
              {config.faqs.map((faq, idx) => <div key={idx} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-2536">
                  <button onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} className="w-full p-4 flex items-center justify-between text-left" data-id="element-2537">
                    <span className="font-bold text-sm text-[#0D3B3B] pr-4" data-id="element-2538">
                      {faq.q}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${openFaqIndex === idx ? 'rotate-180' : ''}`} data-id="element-2539" />
                  </button>
                  <AnimatePresence data-id="element-2540">
                    {openFaqIndex === idx && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-2541">
                        <p className="p-4 pt-0 text-sm text-gray-500 leading-relaxed border-t border-gray-50 mt-2" data-id="element-2542">
                          {faq.a}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>

          {/* CTA - visible on web */}
          <div className="hidden md:block mb-8" data-id="element-2543">
            <button onClick={() => navigate('plan-selection', productId)} className="w-full bg-[#0D3B3B] hover:bg-[#092D2D] text-white font-bold py-4 rounded-xl shadow-lg shadow-emerald-900/20 active:scale-95 transition-transform flex items-center justify-center gap-2" data-id="element-2544">
              View Available Plans <ArrowRightIcon className="w-5 h-5" data-id="element-2545" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>;
}